import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AnnouncementBar from '@/components/feature/AnnouncementBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/feature/ProductCard';
import ImageGallery from './components/ImageGallery';
import ProductDetails from './components/ProductDetails';
import ReviewsSection from './components/ReviewsSection';
import { products } from '@/mocks/products';
import { categories } from '@/mocks/categories';

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'description' | 'reviews'>('description');

  const product = useMemo(() => {
    return products.find((p) => p.slug === slug) || products[0];
  }, [slug]);

  const category = useMemo(() => {
    return categories.find((c) => c.id === product.categoryId);
  }, [product]);

  const relatedProducts = useMemo(() => {
    return products
      .filter((p) => p.categoryId === product.categoryId && p.id !== product.id)
      .slice(0, 4);
  }, [product]);

  // Generate multiple images for gallery using the same product image with different seq
  const galleryImages = useMemo(() => {
    const base = product.images[0];
    if (!base) return [];
    // Return the main image plus 3 variations with different seq params
    return [
      base,
      base.replace(/seq=[^&]+/, 'seq=prod-gallery-01'),
      base.replace(/seq=[^&]+/, 'seq=prod-gallery-02'),
      base.replace(/seq=[^&]+/, 'seq=prod-gallery-03'),
    ];
  }, [product.images]);

  return (
    <div className="min-h-screen bg-background-50">
      <AnnouncementBar />
      <Navbar />

      <main className="pt-28 md:pt-36 pb-12">
        <div className="w-full px-4 md:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6">
            <a href="/" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Home
            </a>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <a href="/shop" className="text-foreground-500 hover:text-primary-600 transition-colors">
              Shop
            </a>
            {category && (
              <>
                <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
                <a
                  href={`/shop/${category.slug}`}
                  className="text-foreground-500 hover:text-primary-600 transition-colors"
                >
                  {category.name}
                </a>
              </>
            )}
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs" />
            <span className="text-foreground-900 font-medium line-clamp-1">{product.name}</span>
          </nav>

          {/* Main Product Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 mb-12">
            <ImageGallery images={galleryImages} productName={product.name} />
            <ProductDetails product={product} />
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="flex items-center gap-1 border-b border-background-200/60">
              <button
                type="button"
                onClick={() => setActiveTab('description')}
                className={`px-4 py-3 text-sm font-semibold transition-colors border-b-2 ${
                  activeTab === 'description'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Description
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('reviews')}
                className={`px-4 py-3 text-sm font-semibold transition-colors border-b-2 ${
                  activeTab === 'reviews'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-foreground-600 hover:text-foreground-900'
                }`}
              >
                Reviews
              </button>
            </div>

            <div className="py-6">
              {activeTab === 'description' ? (
                <div className="space-y-4">
                  <p className="text-sm text-foreground-700 leading-relaxed">{product.description}</p>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-3">Specifications</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { label: 'Brand', value: product.brand },
                        { label: 'SKU', value: product.sku },
                        { label: 'Category', value: product.categoryName },
                        { label: 'Stock', value: `${product.stock} units` },
                        { label: 'Store', value: product.storeName },
                        { label: 'Sold', value: `${product.soldCount.toLocaleString('en-KE')} units` },
                      ].map((spec) => (
                        <div key={spec.label} className="flex justify-between py-2 border-b border-background-200/40">
                          <span className="text-xs text-foreground-500">{spec.label}</span>
                          <span className="text-sm text-foreground-900 font-medium">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div id="reviews">
                  <ReviewsSection productId={product.id} />
                </div>
              )}
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                  Related Products
                </h2>
                <a
                  href={`/shop/${category?.slug || ''}`}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
                >
                  View All
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {relatedProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}